import { Injectable } from '@angular/core';
import { RestService } from './rest-service';
import { Observable } from 'rxjs';
import { ISearchResponse } from 'src/app/core/models/i-search-response';
import { GetDerivedAssetClassesAndSectorsRequest } from 'src/app/core/models/i-get-derived-asset-classes-sectors-request';
import { IManager } from 'src/app/core/models/i-manager';
import { IGetDerivedAssetClassesAndSectorsResponse } from 'src/app/core/models/i-get-derived-asset-classes-sectors-response';
import { IEtf } from 'src/app/core/models/i-etf';
import { ISectorLanguageResourceDto } from 'src/app/core/dtos/interfaces/i-sector-language-resource-dto';
import { IPrivateMarketsFundSearchResponse } from 'src/app/core/models/i-private-markets-fund-search-response';

@Injectable({
  providedIn: 'root',
})
export class ExternalDataService {
  static readonly ENTITY_IDENTIFIER: string = 'externalData';
  private readonly BASE_EXTERNAL_DATA_ROUTE = `api/${ExternalDataService.ENTITY_IDENTIFIER}`;
  private readonly BUILD_HTTP_ROUTE = (...args) =>
    this.restService.BUILD_HTTP_ROUTE(this.BASE_EXTERNAL_DATA_ROUTE, ...args);

  constructor(private restService: RestService) {}

  searchManagers$(searchText: string): Observable<ISearchResponse[]> {
    const params = this.restService.buildHttpParamsObject({
      searchText: String(searchText).trim(),
    });

    return this.restService.get$<ISearchResponse[]>(
      this.BUILD_HTTP_ROUTE('managers'),
      { params }
    );
  }

  searchFunds$(searchText: string): Observable<ISearchResponse[]> {
    const params = this.restService.buildHttpParamsObject({
      searchText: searchText.trim(),
    });

    return this.restService.get$<ISearchResponse[]>(
      this.BUILD_HTTP_ROUTE('funds'),
      { params }
    );
  }

  searchSectors$(searchText: string): Observable<ISearchResponse[]> {
    const params = this.restService.buildHttpParamsObject({
      searchText: searchText.trim(),
    });

    return this.restService.get$<ISearchResponse[]>(
      this.BUILD_HTTP_ROUTE('sectors'),
      { params }
    );
  }

  searchAssetClasses$(searchText: string): Observable<ISearchResponse[]> {
    const params = this.restService.buildHttpParamsObject({
      searchText: searchText.trim(),
    });

    return this.restService.get$<ISearchResponse[]>(
      this.BUILD_HTTP_ROUTE('assetClasses'),
      { params }
    );
  }

  searchEtfs$(searchText: string): Observable<IEtf[]> {
    const params = this.restService.buildHttpParamsObject({
      searchText: searchText.trim(),
    });

    return this.restService.get$<IEtf[]>(this.BUILD_HTTP_ROUTE('etfs'), {
      params,
    });
  }

  searchInvestmentTrusts$(searchText: string): Observable<ISearchResponse[]> {
    const params = this.restService.buildHttpParamsObject({
      searchText: searchText.trim(),
    });

    return this.restService.get$<ISearchResponse[]>(
      this.BUILD_HTTP_ROUTE('investmentTrusts'),
      { params }
    );
  }

  searchShares$(searchText: string): Observable<ISearchResponse[]> {
    const params = this.restService.buildHttpParamsObject({
      searchText: searchText.trim(),
    });

    return this.restService.get$<ISearchResponse[]>(
      this.BUILD_HTTP_ROUTE('shares'),
      { params }
    );
  }

  searchPrivateMarketsFunds$(
    searchText: string
  ): Observable<IPrivateMarketsFundSearchResponse[]> {
    const params = this.restService.buildHttpParamsObject({
      searchText: searchText.trim(),
    });

    // Private Markets funds live under their own top-level route, not the externalData base.
    return this.restService.get$<IPrivateMarketsFundSearchResponse[]>(
      'api/privateMarketsFunds',
      { params }
    );
  }

  derivedAssetClassesAndSectors$(
    fundIds: number[],
    managers: IManager[],
    sectorIds: number[]
  ): Observable<IGetDerivedAssetClassesAndSectorsResponse> {
    const request = new GetDerivedAssetClassesAndSectorsRequest(
      fundIds,
      managers,
      sectorIds
    );

    return this.restService.post$<IGetDerivedAssetClassesAndSectorsResponse>(
      this.BUILD_HTTP_ROUTE('derivedAssetClassesAndSectors'),
      request
    );
  }

  getTranslationsByLocale$(
    locale: string
  ): Observable<ISectorLanguageResourceDto> {
    const params = this.restService.buildHttpParamsObject({
      language: locale.trim(),
    });

    return this.restService.get$<ISectorLanguageResourceDto>(
      this.BUILD_HTTP_ROUTE('translations'),
      { params }
    );
  }
}
